import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, TrendingDown, ShoppingCart, DollarSign, Users, Activity, Calendar, CheckCircle2, Wand2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// QuickAnalytics — interactive KPI block at the bottom of the Admin
// Dashboard. Pulls `/api/admin/analytics/summary` + `/timeseries` for
// the chosen window and renders four clickable KPI cards (orders,
// revenue, AOV, completion-rate) each with a vs-previous-period %
// delta, plus two charts (orders + revenue) with hover tooltips and
// click-through.
//
// Period switcher: Today / 7D / 30D — matches backend `range=today|week|month`.
//
// MVP scope per 2026-06-18 TZ — clickable cards, %-vs-prev, tooltips,
// click chart point → toast. Custom range, drawer-detail, top-blocks
// are explicitly out of scope and live in the backlog.

const RANGES = [
  { key: 'today', label: 'Today',    apiRange: 'today' },
  { key: '7d',    label: '7D',       apiRange: 'week'  },
  { key: '30d',   label: '30D',      apiRange: 'month' },
];

const fetchJSON = (url, token) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'json';
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
      ? resolve(xhr.response || {})
      : reject(new Error(`HTTP ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send();
  });

// Pretty-format a bucket label coming back from the timeseries endpoint.
// Backend buckets are ISO date strings ("2026-06-18") for week/month
// ranges, or hour ints ("00", "01") for today. We render short relative
// dates ("Mon 18") for daily buckets, "HH:00" for hourly.
const fmtBucketLabel = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  // hour bucket
  if (/^\d{1,2}$/.test(s)) return `${s.padStart(2, '0')}:00`;
  // YYYY-MM-DD
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`);
    if (!Number.isNaN(d.getTime())) {
      const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
      return `${wd} ${String(d.getUTCDate()).padStart(2, '0')}`;
    }
  }
  return s;
};

// Display "+12.3%" / "−4.5%" / "—" for a growth pct. Backend returns
// `null` when previous period had 0 data — we render "—" instead of a
// nonsensical infinity. Returned object carries class hints so the
// caller can colour-code without re-computing.
const fmtDelta = (pct, suffix = '%') => {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) {
    return { text: '—', cls: 'text-slate-400', icon: null };
  }
  const rounded = Math.round(pct * 10) / 10;
  if (Math.abs(rounded) < 0.05) return { text: `0${suffix}`, cls: 'text-slate-400', icon: null };
  const sign = rounded > 0 ? '+' : '−';
  const cls = rounded > 0 ? 'text-emerald-600' : 'text-red-600';
  const icon = rounded > 0 ? TrendingUp : TrendingDown;
  return { text: `${sign}${Math.abs(rounded).toFixed(1)}${suffix}`, cls, icon };
};

// One clickable KPI card. Title, big value, sub-line, growth pill,
// onClick → caller-supplied navigate. Hovers raise.
function KpiCard({ label, value, sub, deltaPct, deltaSuffix = '%', icon: Icon, accent, onClick, testid }) {
  const d = fmtDelta(deltaPct, deltaSuffix);
  const DeltaIcon = d.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-3 rounded-lg bg-gradient-to-br ${accent} border hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400`}
    >
      <div className="flex items-start gap-2.5">
        <div className="p-2 rounded-md bg-white/60 backdrop-blur flex-shrink-0">
          <Icon className="w-4 h-4 text-slate-800" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</p>
            <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold tabular-nums ${d.cls}`}>
              {DeltaIcon && <DeltaIcon className="w-3 h-3" />}
              {d.text}
            </span>
          </div>
          <p className="text-base font-bold text-slate-900 leading-tight tabular-nums truncate">{value}</p>
          {sub && <p className="text-[10px] text-slate-500 tabular-nums leading-tight truncate">{sub}</p>}
          <p className="text-[9px] text-slate-400 mt-0.5">vs previous period</p>
        </div>
      </div>
    </button>
  );
}

// Interactive SVG line chart. Bigger than the old sparkline (height
// 100 instead of 36), shows points, has hover tooltip with day +
// number breakdown, and click-through calls onPointClick(bucket).
function LineChart({ buckets, title, stroke, fill, valueFmt, formatTip, onPointClick, testid }) {
  const [hover, setHover] = useState(null); // {idx, x, y}
  const w = 600, h = 100, padX = 16, padY = 14;
  const values = buckets.map((b) => Number(b.value ?? b.count ?? b.amount ?? 0));
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const innerW = w - 2 * padX;
  const innerH = h - 2 * padY;
  const xAt = (i) => padX + (i * innerW) / Math.max(values.length - 1, 1);
  const yAt = (v) => padY + innerH - ((v - min) / range) * innerH;
  const pts = values.map((v, i) => ({ x: xAt(i), y: yAt(v) }));
  const pathLine = pts.length ? `M ${pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ')}` : '';
  const pathArea = pts.length ? `${pathLine} L ${pts[pts.length - 1].x.toFixed(1)},${h - padY} L ${pts[0].x.toFixed(1)},${h - padY} Z` : '';

  // For x-axis labels — show 1st, middle and last bucket so the chart
  // doesn't get crowded but the time axis is always visible.
  const labelIdxs = (() => {
    const n = buckets.length;
    if (n <= 1) return [0];
    if (n <= 4) return buckets.map((_, i) => i);
    return [0, Math.floor(n / 2), n - 1];
  })();

  return (
    <div className="relative p-3 rounded-lg bg-white border border-slate-200" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{title}</p>
        <p className="text-[11px] font-bold text-slate-700 tabular-nums">{valueFmt(values.reduce((a, b) => a + b, 0))}</p>
      </div>
      {buckets.length === 0 ? (
        <div className="h-[100px] flex items-center justify-center text-[11px] text-slate-400">
          No data in this period.
        </div>
      ) : (
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" className="block" preserveAspectRatio="none" style={{ height: 110 }}>
          {/* horizontal grid line in middle for visual reference */}
          <line x1={padX} y1={h / 2} x2={w - padX} y2={h / 2} stroke="#e2e8f0" strokeDasharray="2 3" />
          <path d={pathArea} fill={fill} />
          <path d={pathLine} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" />
          {/* dots — bigger & clickable */}
          {pts.map((p, i) => (
            <g key={i}>
              <circle
                cx={p.x}
                cy={p.y}
                r={hover?.idx === i ? 5 : 3}
                fill={stroke}
                stroke="white"
                strokeWidth="1.5"
                style={{ cursor: 'pointer', transition: 'r 120ms' }}
                onMouseEnter={() => setHover({ idx: i, x: p.x, y: p.y })}
                onMouseLeave={() => setHover(null)}
                onClick={(e) => { e.stopPropagation(); onPointClick && onPointClick(buckets[i], i); }}
                data-testid={`${testid}-point-${i}`}
              />
              {/* invisible bigger hover target so it's easy to grab */}
              <rect
                x={p.x - 12}
                y={padY}
                width={24}
                height={innerH + padY}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHover({ idx: i, x: p.x, y: p.y })}
                onMouseLeave={() => setHover(null)}
                onClick={(e) => { e.stopPropagation(); onPointClick && onPointClick(buckets[i], i); }}
              />
            </g>
          ))}
        </svg>
      )}
      {/* x-axis date labels */}
      {buckets.length > 0 && (
        <div className="flex justify-between mt-0.5 px-1 text-[9px] font-mono text-slate-400">
          {labelIdxs.map((i) => (
            <span key={i}>{fmtBucketLabel(buckets[i]?.label)}</span>
          ))}
        </div>
      )}
      {/* tooltip */}
      {hover && (
        <div
          className="absolute z-10 pointer-events-none bg-slate-900 text-white text-[10px] rounded shadow-lg px-2 py-1 whitespace-nowrap font-mono"
          style={{
            left: `calc(${(hover.x / w) * 100}% + 8px)`,
            top: `calc(${(hover.y / h) * 100}% - 8px)`,
            transform: 'translate(-50%, -100%)',
          }}
          data-testid={`${testid}-tooltip`}
        >
          {formatTip(buckets[hover.idx])}
        </div>
      )}
    </div>
  );
}

export default function QuickAnalytics({ token, onOpenAnalytics, navigateWithFilter, go }) {
  const [range, setRange] = useState('7d');
  const [summary, setSummary]   = useState(null);
  const [ordersTs, setOrdersTs] = useState(null);
  const [revenueTs, setRevenueTs] = useState(null);
  const [topVehicles, setTopVehicles] = useState(null);
  const [topCustomers, setTopCustomers] = useState(null);
  const [problemQueue, setProblemQueue] = useState(null);
  // ECU-finder adoption (% of orders placed via the inline Find-by-ECU
  // smart selector in the selected window). Admin-only telemetry —
  // lets us measure how many customers needed the helper to identify
  // their vehicle and gauge whether the feature deserves more
  // promotion in the UI.
  const [ecuFinderAdoption, setEcuFinderAdoption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Per-day breakdown shown after clicking a chart point. Anchored
  // under the relevant chart, dismissed on outside click.
  const [pointDetail, setPointDetail] = useState(null);

  const apiRange = RANGES.find((r) => r.key === range)?.apiRange || 'week';

  const reload = useCallback(async () => {
    setLoading(true);
    setErr('');
    setPointDetail(null);
    try {
      const [s, o, r, tv, tc, pq, ef] = await Promise.all([
        fetchJSON(`${API_URL}/api/admin/analytics/summary?range=${apiRange}`, token),
        fetchJSON(`${API_URL}/api/admin/analytics/timeseries?metric=orders&range=${apiRange}`, token),
        fetchJSON(`${API_URL}/api/admin/analytics/timeseries?metric=revenue&range=${apiRange}`, token),
        fetchJSON(`${API_URL}/api/admin/analytics/top-vehicles?range=${apiRange}&limit=5`, token).catch(() => null),
        fetchJSON(`${API_URL}/api/admin/analytics/orders/top-customers?range=${apiRange}&metric=revenue&limit=5`, token).catch(() => null),
        // problem-queue is global (not per-range) — ignore the range here
        fetchJSON(`${API_URL}/api/admin/analytics/problem-queue`, token).catch(() => null),
        fetchJSON(`${API_URL}/api/admin/analytics/ecu-finder-adoption?range=${apiRange}&series=true`, token).catch(() => null),
      ]);
      setSummary(s);
      setOrdersTs(o);
      setRevenueTs(r);
      setTopVehicles(tv);
      setTopCustomers(tc);
      setProblemQueue(pq);
      setEcuFinderAdoption(ef);
    } catch (e) {
      setErr(e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [apiRange, token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { await reload(); }
      catch (_) { /* reload sets err */ }
      if (!alive) return;
    })();
    return () => { alive = false; };
  }, [reload]);

  // ── Derived values ────────────────────────────────────────────
  const ordersTotal     = summary?.orders?.total ?? 0;
  const ordersRevenue   = summary?.orders?.revenue ?? 0;
  const ordersAov       = summary?.orders?.aov ?? 0;
  const byStatus        = summary?.orders?.by_status || {};
  const completedCount  = byStatus.completed?.count ?? 0;
  const cancelledCount  = byStatus.cancelled?.count ?? 0;
  const completionPct   = ordersTotal > 0 ? Math.round((completedCount / ordersTotal) * 100) : null;
  const rechargesAmount = summary?.recharges?.real_total ?? summary?.recharges?.total ?? 0;
  const rechargesCount  = summary?.recharges?.count ?? 0;
  const growth          = summary?.growth || {};

  const ordersBuckets  = ordersTs?.buckets || [];
  const revenueBuckets = revenueTs?.buckets || [];

  // ── Click handlers ────────────────────────────────────────────
  const nav = (page, opts) => {
    if (navigateWithFilter) navigateWithFilter(page, opts || {});
    else if (go) go(page);
  };

  return (
    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-testid="quick-analytics">
      {/* Header with period switcher */}
      <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-600" />
          <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Quick analytics</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Period switcher */}
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="quick-analytics-range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                data-testid={`quick-analytics-range-${r.key}`}
                className={`px-2.5 py-1 text-[11px] font-semibold transition-colors focus:outline-none ${
                  range === r.key
                    ? 'bg-cyan-500 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={onOpenAnalytics}
            data-testid="quick-analytics-open-full"
            className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 hover:underline whitespace-nowrap"
          >
            Full Analytics →
          </button>
        </div>
      </div>

      {/* Loading / error / empty */}
      {loading && (
        <div className="py-6 text-center text-xs text-slate-500" data-testid="quick-analytics-loading">
          Loading analytics for {RANGES.find((r) => r.key === range)?.label}…
        </div>
      )}
      {!loading && err && (
        <div className="py-4 px-3 rounded bg-red-50 border border-red-200 text-xs text-red-700 flex items-center justify-between" data-testid="quick-analytics-error">
          <span>Could not load analytics ({err})</span>
          <button onClick={reload} className="font-bold underline hover:no-underline">Retry</button>
        </div>
      )}
      {!loading && !err && ordersTotal === 0 && ordersBuckets.every((b) => (b.value ?? b.count ?? 0) === 0) && (
        <div className="py-4 px-3 rounded bg-slate-50 border border-slate-200 text-xs text-slate-500 text-center" data-testid="quick-analytics-empty">
          No data in the selected period.
        </div>
      )}

      {!loading && !err && (ordersTotal > 0 || ordersBuckets.length > 0) && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
            <KpiCard
              label="Orders"
              value={ordersTotal}
              sub={`€${Number(ordersRevenue).toFixed(2)} revenue`}
              deltaPct={growth.orders_pct}
              icon={ShoppingCart}
              accent="from-blue-50 to-cyan-50 border-cyan-200"
              onClick={() => nav('orders', {})}
              testid="quick-analytics-card-orders"
            />
            <KpiCard
              label="Recharges"
              value={`€${Number(rechargesAmount).toFixed(2)}`}
              sub={`${rechargesCount} tx`}
              deltaPct={growth.recharges_pct}
              icon={DollarSign}
              accent="from-emerald-50 to-green-50 border-emerald-200"
              onClick={() => nav('transactions', {})}
              testid="quick-analytics-card-recharges"
            />
            <KpiCard
              label="AOV"
              value={`€${Number(ordersAov).toFixed(2)}`}
              sub="avg order value"
              deltaPct={growth.aov_pct}
              icon={Users}
              accent="from-purple-50 to-fuchsia-50 border-purple-200"
              onClick={() => nav('orders', {})}
              testid="quick-analytics-card-aov"
            />
            <KpiCard
              label="Completion"
              value={completionPct !== null ? `${completionPct}%` : '—'}
              sub={`${completedCount} of ${ordersTotal} completed`}
              deltaPct={growth.completion_pp /* pp = percentage points */}
              deltaSuffix="pp"
              icon={CheckCircle2}
              accent="from-amber-50 to-orange-50 border-amber-200"
              onClick={() => nav('orders', { ordersStatus: 'completed' })}
              testid="quick-analytics-card-completion"
            />
            {/* Smart ECU-finder adoption — % of orders in the selected
                window where the customer used the inline Find-by-ECU
                helper on Step 1. Click → orders list filtered to those
                orders so the admin can review them. Renders a small
                inline sparkline below the count when bucket data is
                available so the admin sees the trend at a glance. */}
            <button
              type="button"
              data-testid="quick-analytics-card-ecu-finder"
              onClick={() => nav('orders', { ecuFinderOnly: true })}
              title="Customers who used the Find-by-ECU smart selector on Step 1. Click to filter Orders."
              className="text-left p-3 rounded-lg bg-gradient-to-br from-cyan-50 to-sky-100 border border-cyan-300 hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400"
            >
              <div className="flex items-start gap-2.5">
                <div className="p-2 rounded-md bg-white/60 backdrop-blur flex-shrink-0">
                  <Wand2 className="w-4 h-4 text-cyan-800" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 truncate">ECU Finder</p>
                  <p className="text-lg font-extrabold text-slate-800 tabular-nums leading-tight">
                    {(() => {
                      const pct = ecuFinderAdoption?.adoption_pct;
                      return (pct === null || pct === undefined) ? '—' : `${pct}%`;
                    })()}
                  </p>
                  <p className="text-[10px] text-slate-500 truncate mt-0.5">
                    {ecuFinderAdoption?.via_finder ?? 0} of {ecuFinderAdoption?.total ?? 0} via finder
                  </p>
                </div>
              </div>
              {/* Inline sparkline — same logic as AnalyticsPanel
                  but compressed to fit the smaller card footprint. */}
              {(() => {
                const buckets = Array.isArray(ecuFinderAdoption?.buckets) ? ecuFinderAdoption.buckets : [];
                const pts = buckets
                  .map((b, i) => ({ x: i, y: b.adoption_pct }))
                  .filter(p => p.y !== null && p.y !== undefined && Number.isFinite(p.y));
                if (pts.length < 2) return null;
                const W = 100, H = 20, PAD = 2;
                const step = (W - PAD * 2) / Math.max(buckets.length - 1, 1);
                const pathD = pts.map((p, i) =>
                  `${i === 0 ? 'M' : 'L'} ${(PAD + p.x * step).toFixed(2)} ${(H - PAD - (p.y / 100) * (H - PAD * 2)).toFixed(2)}`
                ).join(' ');
                const last = pts[pts.length - 1];
                return (
                  <svg
                    data-testid="quick-analytics-card-ecu-finder-sparkline"
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="none"
                    className="w-full h-5 mt-1.5"
                    aria-hidden="true"
                  >
                    <path d={pathD} fill="none" stroke="#0891b2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx={(PAD + last.x * step).toFixed(2)} cy={(H - PAD - (last.y / 100) * (H - PAD * 2)).toFixed(2)} r="1.6" fill="#0891b2" />
                  </svg>
                );
              })()}
            </button>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <LineChart
              testid="quick-analytics-chart-orders"
              buckets={ordersBuckets}
              title={`Orders · ${RANGES.find((r) => r.key === range)?.label}`}
              stroke="#0891b2"
              fill="rgba(8,145,178,0.12)"
              valueFmt={(t) => `${t} total`}
              formatTip={(b) => `${fmtBucketLabel(b?.label)} · ${b?.count ?? b?.value ?? 0} orders · €${Number(b?.total_amount ?? b?.value ?? 0).toFixed(2)}`}
              onPointClick={(b, i) => setPointDetail({ kind: 'orders', bucket: b, i })}
            />
            <LineChart
              testid="quick-analytics-chart-revenue"
              buckets={revenueBuckets}
              title={`Revenue · ${RANGES.find((r) => r.key === range)?.label}`}
              stroke="#10b981"
              fill="rgba(16,185,129,0.12)"
              valueFmt={(t) => `€${Number(t).toFixed(2)}`}
              formatTip={(b) => `${fmtBucketLabel(b?.label)} · €${Number(b?.amount ?? b?.value ?? 0).toFixed(2)} · ${b?.count ?? 0} orders`}
              onPointClick={(b, i) => setPointDetail({ kind: 'revenue', bucket: b, i })}
            />
          </div>

          {/* Point-detail mini-card — shown when a chart point is clicked.
              Stays in-flow under the charts so it doesn't cover anything;
              a small "open in Orders →" link is the click-through. */}
          {pointDetail && (
            <div
              className="mt-3 p-3 rounded-lg bg-gradient-to-r from-slate-50 to-blue-50 border border-blue-200"
              data-testid="quick-analytics-point-detail"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {fmtBucketLabel(pointDetail.bucket?.label)} · {pointDetail.kind === 'orders' ? 'Orders breakdown' : 'Revenue breakdown'}
                  </p>
                  <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[11px] tabular-nums">
                    <div><span className="text-slate-500">Orders:</span> <span className="font-bold text-slate-800">{pointDetail.bucket?.count ?? pointDetail.bucket?.value ?? 0}</span></div>
                    <div><span className="text-slate-500">Revenue:</span> <span className="font-bold text-slate-800">€{Number(pointDetail.bucket?.amount ?? pointDetail.bucket?.total_amount ?? 0).toFixed(2)}</span></div>
                    <div><span className="text-slate-500">Completed:</span> <span className="font-bold text-emerald-700">{pointDetail.bucket?.completed_count ?? 0}</span></div>
                    <div><span className="text-slate-500">Cancelled:</span> <span className="font-bold text-red-700">{pointDetail.bucket?.cancelled_count ?? 0}</span></div>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
                  <button
                    onClick={() => nav(pointDetail.kind === 'revenue' ? 'transactions' : 'orders', {})}
                    data-testid="quick-analytics-point-detail-open"
                    className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 hover:underline whitespace-nowrap"
                  >
                    Open in {pointDetail.kind === 'revenue' ? 'Transactions' : 'Orders'} →
                  </button>
                  <button
                    onClick={() => setPointDetail(null)}
                    className="text-[11px] text-slate-400 hover:text-slate-600"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ─────────────── Operational mini-blocks ───────────────
              Three at-a-glance lists giving the admin an instant pulse
              of the business state. Each block has its own data source
              and degrades gracefully (empty state) if the fetch failed
              — we don't want one slow query to hide the others. */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="quick-analytics-mini-blocks">
            {/* Top Vehicles */}
            <div className="p-3 rounded-lg bg-white border border-slate-200" data-testid="quick-analytics-top-vehicles">
              <div className="flex items-center justify-between mb-2 gap-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Top vehicles</p>
                <span className="text-[9px] text-slate-400">{RANGES.find((r) => r.key === range)?.label}</span>
              </div>
              {!topVehicles || (topVehicles.items || []).length === 0 ? (
                <p className="text-[11px] text-slate-400 py-2 text-center">No vehicle data for this period.</p>
              ) : (
                <ol className="space-y-1.5">
                  {topVehicles.items.map((v, i) => (
                    <li key={`${v.label}-${i}`} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`quick-analytics-top-vehicle-${i}`}>
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="font-mono font-bold text-slate-400 w-3 text-right">{i + 1}.</span>
                        <span className="truncate text-slate-800 font-medium">{v.label}</span>
                      </span>
                      <span className="text-slate-600 tabular-nums whitespace-nowrap">
                        {v.count} · <span className="font-bold">€{Number(v.revenue).toFixed(0)}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* Top Customers (by revenue) */}
            <div className="p-3 rounded-lg bg-white border border-slate-200" data-testid="quick-analytics-top-customers">
              <div className="flex items-center justify-between mb-2 gap-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Top customers</p>
                <span className="text-[9px] text-slate-400">by revenue · {RANGES.find((r) => r.key === range)?.label}</span>
              </div>
              {!topCustomers || (topCustomers.items || []).length === 0 ? (
                <p className="text-[11px] text-slate-400 py-2 text-center">No customer data for this period.</p>
              ) : (
                <ol className="space-y-1.5">
                  {topCustomers.items.slice(0, 5).map((c, i) => {
                    const label = [c.first_name, c.surname].filter(Boolean).join(' ').trim() || c.user_email || '—';
                    return (
                      <li key={`${c.user_id}-${i}`} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`quick-analytics-top-customer-${i}`}>
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className="font-mono font-bold text-slate-400 w-3 text-right">{i + 1}.</span>
                          <span className="truncate text-slate-800 font-medium" title={c.user_email}>{label}</span>
                        </span>
                        <span className="text-slate-600 tabular-nums whitespace-nowrap">
                          {c.orders} · <span className="font-bold">€{Number(c.revenue).toFixed(0)}</span>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* Problem Queue */}
            <div className="p-3 rounded-lg bg-white border border-slate-200" data-testid="quick-analytics-problem-queue">
              <div className="flex items-center justify-between mb-2 gap-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Problem queue</p>
                <span className="text-[9px] text-slate-400">live</span>
              </div>
              {!problemQueue ? (
                <p className="text-[11px] text-slate-400 py-2 text-center">Loading…</p>
              ) : (
                <ul className="space-y-1.5">
                  <ProblemRow label="Open tickets" value={problemQueue.open_tickets ?? 0} onClick={() => nav('tickets', {})} testid="pq-open-tickets" />
                  <ProblemRow label="Pending orders" value={problemQueue.pending_orders ?? 0} onClick={() => nav('orders', { ordersStatus: 'pending' })} testid="pq-pending-orders" />
                  <ProblemRow label="Failed payments" value={problemQueue.failed_payments ?? 0} onClick={() => nav('transactions', {})} testid="pq-failed-payments" />
                  <ProblemRow label="Stuck EVC" value={problemQueue.stuck_evc ?? 0} onClick={() => nav('evc', {})} testid="pq-stuck-evc" />
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Single row in the Problem Queue block. Renders red when non-zero,
// muted-green ("OK ✓") when zero. Click navigates to the relevant
// admin page so the admin can act on it without searching.
function ProblemRow({ label, value, onClick, testid }) {
  const trouble = value > 0;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-[11px] hover:bg-slate-50 focus:outline-none focus:bg-slate-50 ${trouble ? 'text-red-700' : 'text-slate-600'}`}
      >
        <span className="font-medium">{label}</span>
        <span className={`font-mono font-bold tabular-nums ${trouble ? 'text-red-700' : 'text-emerald-600'}`}>
          {trouble ? value : '✓'}
        </span>
      </button>
    </li>
  );
}
